import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminPlacementTestSessionById } from '../../shared/api/placement-tests.services';
import toast from '../../shared/lib/toast';
import StatusBadge from '../../shared/ui/status-badge/StatusBadge';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('uk-UA')} ${date.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function formatDuration(durationMs) {
  if (!durationMs || durationMs < 0) return '—';
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes} хв ${seconds} с` : `${seconds} с`;
}

const COLORS = {
  correct: { bg: '#EAF6EA', border: '#2D9E6B', text: '#2C5140' },
  wrong: { bg: '#FFE8E8', border: '#FE0707', text: '#B91C1C' },
  neutral: { bg: '#F8FAFC', border: '#E2E8F0', text: '#64748B' },
};

// Разбор сессии выглядит одинаково для взрослого и детского теста, поэтому
// страница одна, а раздел админки передаёт свой запрос за сессией: у детского
// теста это отдельный эндпоинт, который не отдаёт чужие сессии.
export default function PlacementTestSessionDetailPage({
  loadSession = getAdminPlacementTestSessionById,
}) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const fetchSession = async () => {
      setLoading(true);
      try {
        const data = await loadSession(sessionId);
        if (active) {
          setSession(data);
        }
      } catch (error) {
        console.error('Error fetching session detail:', error);
        toast.error('Помилка при завантаженні сесії');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchSession();

    return () => {
      active = false;
    };
  }, [sessionId, loadSession]);

  if (loading) {
    return (
      <section className="section-admin">
        <p>Завантаження...</p>
      </section>
    );
  }

  if (!session) {
    return (
      <section className="section-admin">
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: '1px solid #D9D9D9',
            borderRadius: '5px',
            padding: '8px 16px',
            cursor: 'pointer',
            marginBottom: '16px',
          }}
        >
          ← Назад
        </button>
        <p>Сесію не знайдено.</p>
      </section>
    );
  }

  const review = Array.isArray(session.review) ? session.review : [];
  const correctCount = review.filter((item) => item.isCorrect).length;
  // Міні-тест — опитування без правильних відповідей: сервер не віддає по ньому
  // ні оцінку питання, ні правильний варіант, тож і показувати нічого.
  const isInformational = Boolean(session.isInformational);
  const answeredCount = review.filter((item) => item.answered).length;

  return (
    <section className="section-admin">
      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'none',
          border: '1px solid #D9D9D9',
          borderRadius: '5px',
          padding: '8px 16px',
          cursor: 'pointer',
          marginBottom: '24px',
        }}
      >
        ← Назад до сесій
      </button>

      {/* Загальна інформація */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '24px',
          padding: '24px',
          border: '1px solid #D9D9D9',
          borderRadius: '12px',
          marginBottom: '24px',
          background: '#fff',
        }}
      >
        <div>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Імʼя</p>
          <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
            {session.contact?.name || 'N/A'}
          </p>
        </div>
        <div>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Телефон</p>
          <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
            {session.contact?.phone || 'N/A'}
          </p>
        </div>
        {session.contact?.email && (
          <div>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Email</p>
            <p style={{ fontSize: '15px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
              {session.contact.email}
            </p>
          </div>
        )}
        {session.contact?.preferredChannel && (
          <div>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Зручний зв'язок</p>
            <p style={{ fontSize: '15px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
              {session.contact.preferredChannel}
            </p>
          </div>
        )}
        {/* Вік, рівень і результат — тільки для головного тесту: в опитуванні
            віку не питають, а оцінки в нього немає за визначенням. */}
        {!isInformational && (
          <>
            <div>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Вік</p>
              <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
                {session.contact?.age ?? 'N/A'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Рівень</p>
              <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
                {session.result?.level || 'N/A'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Результат</p>
              <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
                {session.result
                  ? `${session.result.score}/${session.result.maxScore} (${session.result.percentage}%)`
                  : 'N/A'}
              </p>
            </div>
          </>
        )}
        <div>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Статус</p>
          <StatusBadge status={session.status} />
        </div>
        <div>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Дата</p>
          <p style={{ fontSize: '15px', margin: 0, color: '#0F172A' }}>
            {formatDateTime(session.createdAt)}
          </p>
        </div>
        <div>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>Тривалість</p>
          <p style={{ fontSize: '15px', margin: 0, color: '#0F172A' }}>
            {formatDuration(session.durationMs)}
          </p>
        </div>
        <div>
          <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>
            {isInformational ? 'Відповідей' : 'Правильних'}
          </p>
          <p style={{ fontSize: '15px', margin: 0, color: '#0F172A' }}>
            {isInformational ? answeredCount : correctCount} / {review.length}
          </p>
        </div>
      </div>

      {/* Результати по питаннях */}
      <h2 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 16px 0', color: '#0F172A' }}>
        Відповіді по питаннях
      </h2>

      {review.length === 0 ? (
        <p style={{ color: '#94A3B8' }}>Немає даних по питаннях.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {review.map((item) => {
            const statusColor =
              !item.answered || isInformational
                ? COLORS.neutral
                : item.isCorrect
                ? COLORS.correct
                : COLORS.wrong;

            return (
              <div
                key={item.questionId}
                style={{
                  border: `1px solid ${statusColor.border}`,
                  borderLeft: `4px solid ${statusColor.border}`,
                  borderRadius: '10px',
                  padding: '20px',
                  background: '#fff',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '16px',
                  }}
                >
                  <div>
                    <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 6px 0' }}>
                      Питання {item.order}
                    </p>
                    <p style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#0F172A' }}>
                      {item.prompt}
                    </p>
                    {item.helperText && (
                      <p style={{ fontSize: '13px', color: '#94A3B8', margin: '6px 0 0 0' }}>
                        {item.helperText}
                      </p>
                    )}
                  </div>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: '13px',
                      fontWeight: 600,
                      padding: '4px 12px',
                      borderRadius: '999px',
                      background: statusColor.bg,
                      color: statusColor.text,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {!item.answered
                      ? 'Без відповіді'
                      : isInformational
                      ? 'Є відповідь'
                      : `${item.isCorrect ? '✓ Правильно' : '✗ Помилка'} · ${item.earnedScore}/${item.maxScore}`}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {item.groups.map((group) => {
                    const isWrong =
                      !isInformational && group.selected && !group.selected.isCorrect;
                    const isCorrect =
                      !isInformational && group.selected && group.selected.isCorrect;

                    return (
                      <div
                        key={group.groupId}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          background: '#F8FAFC',
                        }}
                      >
                        {group.label && (
                          <p
                            style={{
                              fontSize: '13px',
                              color: '#64748B',
                              margin: '0 0 8px 0',
                            }}
                          >
                            {group.label}
                          </p>
                        )}

                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                              {isInformational ? 'Відповідь: ' : 'Обрано: '}
                            </span>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: isWrong
                                  ? COLORS.wrong.text
                                  : isCorrect
                                  ? COLORS.correct.text
                                  : '#64748B',
                              }}
                            >
                              {group.selected ? group.selected.text : '— (немає відповіді)'}
                            </span>
                          </div>

                          {/* Правильну відповідь показуємо, якщо обрано неправильно або
                              зовсім не обрано. В опитуванні правильних немає взагалі. */}
                          {!isInformational && (!group.selected || isWrong) && group.correct && (
                            <div>
                              <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                                Правильно:{' '}
                              </span>
                              <span
                                style={{
                                  fontSize: '15px',
                                  fontWeight: 600,
                                  color: COLORS.correct.text,
                                }}
                              >
                                {Array.isArray(group.correct.acceptedAnswers)
                                  ? group.correct.acceptedAnswers.join(' / ')
                                  : group.correct.text}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
